import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle, ArrowClockwise, CaretDown, PaperPlaneTilt, ChatCircleDots,
} from "@phosphor-icons/react";

const MODEL_OPTIONS = [
  { key: "flash-35", label: "Gemini 3.5 Flash · fast" },
  { key: "flash",    label: "Gemini 3 Flash · quick" },
  { key: "pro",      label: "Gemini 3.1 Pro · deep" },
];

const PRIORITY_STYLE = {
  high: "bg-[#fee2e2] text-[#991b1b]",
  med:  "bg-[#fef3c7] text-[#92400e]",
  low:  "bg-[#dcfce7] text-[#166534]",
};

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function ExecutiveInsights() {
  const [model, setModel] = useState("flash-35");
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showQA, setShowQA] = useState(false);
  const [question, setQuestion] = useState("");
  const [qa, setQa] = useState([]); // {role, text, at}
  const [asking, setAsking] = useState(false);

  async function loadLatest() {
    setLoading(true);
    try {
      const { data } = await api.get("/insights/latest");
      if (data && data.result) {
        setBrief(data);
        setQa((data.qa || []).flatMap((t) => ([
          { role: "user", text: t.question, at: t.at },
          { role: "ai",   text: t.answer,   at: t.at },
        ])));
      } else {
        setBrief(null);
      }
    } catch (e) {
      // 404-ish or first run — silent
    }
    setLoading(false);
  }

  useEffect(() => { loadLatest(); }, []);

  async function generate() {
    setGenerating(true);
    try {
      const { data } = await api.post("/insights/executive", { model });
      setBrief(data);
      setQa([]);
      toast.success("Fresh executive brief ready");
    } catch (e) { toast.error(formatError(e)); }
    setGenerating(false);
  }

  async function ask(e) {
    e?.preventDefault?.();
    const q = question.trim();
    if (!q || !brief) return;
    setAsking(true);
    setQa((prev) => [...prev, { role: "user", text: q, at: new Date().toISOString() }]);
    setQuestion("");
    try {
      const { data } = await api.post("/insights/ask", {
        brief_id: brief.id,
        question: q,
        model,
      });
      setQa((prev) => [...prev, { role: "ai", text: data.answer, at: data.at }]);
    } catch (e) {
      toast.error(formatError(e));
      setQa((prev) => prev.slice(0, -1)); // drop the optimistic user msg
    }
    setAsking(false);
  }

  if (loading) {
    return (
      <div className="phizo-card p-5 mb-6" data-testid="insights-panel">
        <div className="phizo-label">Loading executive brief…</div>
      </div>
    );
  }

  const result  = brief?.result || {};
  const kpis    = result.kpis    || [];
  const actions = result.actions || [];

  return (
    <div
      className="mb-6 border border-[#e4e4e7] bg-gradient-to-br from-[#0B1224] via-[#0B1224] to-[#111a35] text-white overflow-hidden"
      data-testid="insights-panel"
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#FFD400] text-[#0B1224]">
            <Sparkle weight="fill" size={22} />
          </div>
          <div>
            <div className="phizo-label text-[10px] text-[#FFD400]">Executive insights · powered by Gemini</div>
            <h3 className="font-heading text-xl font-black tracking-tight">
              {result.headline || (brief ? "Insight ready" : "Nothing to review yet")}
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="appearance-none bg-white/10 text-white text-xs px-3 py-2 pr-8 border border-white/20 outline-none focus:border-white cursor-pointer"
              data-testid="insight-model-select"
            >
              {MODEL_OPTIONS.map((m) => (
                <option key={m.key} value={m.key} className="text-black">
                  {m.label}
                </option>
              ))}
            </select>
            <CaretDown size={12} weight="bold" className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          <button
            type="button"
            onClick={generate}
            disabled={generating}
            data-testid="insight-generate-btn"
            className="bg-[#FFD400] text-[#0B1224] px-4 py-2 text-xs font-bold uppercase tracking-wide inline-flex items-center gap-2 disabled:opacity-60"
          >
            <ArrowClockwise size={14} weight="bold" className={generating ? "animate-spin" : ""} />
            {generating ? "Thinking…" : (brief ? "Regenerate" : "Generate brief")}
          </button>
        </div>
      </div>

      {!brief ? (
        <div className="px-6 py-10 text-center text-white/70" data-testid="insight-empty">
          <div className="phizo-label text-[11px] tracking-wide mb-2">No brief yet</div>
          <p className="text-sm max-w-md mx-auto">
            Generate an AI-powered snapshot of production, stock, purchasing and payroll — with concrete action items you can hand off in seconds.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-b border-white/10">
            <div className="lg:col-span-2 p-6 border-r border-white/10">
              <div className="phizo-label text-[10px] text-white/50 mb-2">Executive brief</div>
              <p className="text-sm leading-relaxed text-white/90 whitespace-pre-line" data-testid="insight-brief-text">
                {result.brief || "—"}
              </p>
              {kpis.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-5" data-testid="insight-kpis">
                  {kpis.map((k, idx) => (
                    <div key={idx} className="bg-white/5 border border-white/10 px-3 py-2">
                      <div className="phizo-label text-[9px] text-white/50">{k.label}</div>
                      <div className="mono text-base font-bold">{k.value}</div>
                      {k.note && <div className="text-[11px] text-white/60 mt-0.5 leading-snug">{k.note}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6">
              <div className="phizo-label text-[10px] text-white/50 mb-3">Recommended actions</div>
              {actions.length === 0 ? (
                <div className="text-xs text-white/60">No actions surfaced.</div>
              ) : (
                <ul className="space-y-3" data-testid="insight-actions">
                  {actions.map((a, idx) => (
                    <li key={idx} className="border border-white/10 bg-white/5 p-3">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-bold text-sm leading-tight">{a.title}</div>
                        <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 ${PRIORITY_STYLE[a.priority] || PRIORITY_STYLE.med}`}>
                          {a.priority || "med"}
                        </span>
                      </div>
                      {a.why && <div className="text-[11px] text-white/70 leading-relaxed">{a.why}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="px-6 py-3 flex items-center justify-between text-[11px] text-white/50 border-b border-white/10">
            <div>
              Generated {fmtWhen(brief.generated_at)} · model <span className="mono">{brief.model}</span>
            </div>
            <button
              type="button"
              onClick={() => setShowQA((v) => !v)}
              data-testid="insight-toggle-qa"
              className="inline-flex items-center gap-1 hover:text-white"
            >
              <ChatCircleDots size={13} weight="bold" />
              {showQA ? "Hide chat" : "Ask a follow-up"}
            </button>
          </div>

          {showQA && (
            <div className="px-6 py-4 bg-white/5" data-testid="insight-qa">
              {qa.length > 0 && (
                <div className="mb-3 space-y-2 max-h-56 overflow-y-auto pr-1">
                  {qa.map((m, idx) => (
                    <div
                      key={idx}
                      className={m.role === "user"
                        ? "text-xs bg-[#FFD400]/15 border border-[#FFD400]/40 p-2"
                        : "text-xs bg-white/5 border border-white/10 p-2 whitespace-pre-line"}
                    >
                      <div className="phizo-label text-[9px] text-white/50 mb-0.5">
                        {m.role === "user" ? "You" : "LUROMA AI"}
                      </div>
                      {m.text}
                    </div>
                  ))}
                </div>
              )}
              <form onSubmit={ask} className="flex items-center gap-2">
                <input
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask about production, stock, POs, payroll…"
                  className="flex-1 bg-white text-black text-sm px-3 py-2 border border-white/20 outline-none"
                  data-testid="insight-qa-input"
                  disabled={asking}
                />
                <button
                  type="submit"
                  disabled={asking || !question.trim()}
                  className="bg-[#FFD400] text-[#0B1224] px-3 py-2 text-xs font-bold uppercase inline-flex items-center gap-1 disabled:opacity-60"
                  data-testid="insight-qa-send"
                >
                  <PaperPlaneTilt size={13} weight="bold" />
                  {asking ? "…" : "Ask"}
                </button>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
}
